import * as React from 'react';
import styled from 'styled-components';
import IAdditionStyle from '../../models/IAdditionStyle';
import Radio from '../radio/Radio';

// ############################ Styles ###########################
// tslint:disable:variable-name
const STableContainer = styled.div(({ theme }) => {
  return `
  display: flex;
  flex-direction: column;
  `;
});

const STableRow = styled.div<{ isHeader: boolean }>(
  ({ theme, isHeader }) => `
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  
  font-size: ${isHeader ? theme.font.size.smallMedium : theme.font.size.smallMedium2};
  font-weight: ${theme.font.weight.light};
  font-stretch: normal;
  font-style: normal;
  line-height: ${isHeader ? '1.47' : '1.38'};
  letter-spacing: normal;
  padding: 18px 25px;
  
  color: ${theme.colors.grayLight2};
  border-bottom: 2px solid ${theme.colors.tableBorderBackground};
  background-color: ${isHeader ? theme.colors.tableHeaderBackground : 'white'};
  `
);

const STableTitle = styled.div(({ theme }) => {
  return `
  margin-right: ${theme.spacing.larger2}px;
  flex: 1;  

  &:last-child {
    margin-right: 0;
  }
  `;
});

const STableLink = styled.a(({ theme }) => {
  return `
  margin-right: ${theme.spacing.larger2}px;
  flex: 1;  
  color: ${theme.colors.light};
  &:last-child {
    margin-right: 0;
  }
  `;
});

const STableColumn = styled(STableTitle)<{
  isBold: boolean;
  addRadioSpace: boolean;
}>(
  ({ theme, isBold, addRadioSpace }) => `
  color: ${isBold ? theme.colors.primaryDark2 : theme.colors.grayLight2};
  font-weight: ${isBold ? theme.font.weight.bold : theme.font.weight.light};
  padding-left: ${addRadioSpace ? '41px' : 0};
  `
);

const SEmptyTable = styled.div(({ theme }) => {
  return `
  font-size: ${theme.font.size.smallMedium2};
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.38;
  display: flex;
  align-items: center;
  letter-spacing: normal;
  color: ${theme.colors.error2};
  position: relative;
  padding-left: ${theme.spacing.larger2}px;

  &:before {
    content: '';
    background: url(${theme.icons.invalidName}) no-repeat;
    left: 0;  
    width: 29px;
    height: 26px;
    position: absolute;
  }
  `;
});

// tslint:enable:variable-name
// ############################ End Styles ###########################
export interface ITableList {
  [key: string]: string;
  creationTime: string;
  retailerName: string;
  amount: string;
  phoneNumber?: string;
  zipCode: string;
  declineKey: string;
}

export interface ITableProps {
  children: Array<ITableList>;
  tableHeaderList: Map<string, string>;
  disabledRowCondition?: (row: any) => boolean;
  dataAt: string;
  style?: IAdditionStyle;
  fieldBold?: string;
  tableLabel: string;
  isRadioOption?: boolean;
  isRadioSelected?: number;
  emptyResultsText?: string;
  onSelectedItem?: (index: number) => void;
  getElementWithNewStyle?: (row?: any, key?: any) => JSX.Element | null;
}

export default ({
  children,
  tableHeaderList,
  disabledRowCondition = () => false,
  getElementWithNewStyle = () => null,
  dataAt,
  style = {},
  fieldBold = '',
  tableLabel,
  isRadioOption = false,
  isRadioSelected = -1,
  emptyResultsText = null,
  onSelectedItem,
}: ITableProps) => {
  const tableTitlesList = [...tableHeaderList];

  const getElement = (row, key) => {
    if (getElementWithNewStyle && getElementWithNewStyle(row, key)) {
      // return element with new style
      return getElementWithNewStyle(row, key);
    }

    // return element with default style
    return row[key];
  };

  const printTableHeader = () => {
    return (
      <STableRow isHeader={true}>
        {tableTitlesList.map((title, index) => (
          <STableColumn addRadioSpace={isRadioOption && index === 0} key={index} isBold={false}>
            {title[1]}
          </STableColumn>
        ))}
      </STableRow>
    );
  };

  const printTableColumn = (rowIndex, columnIndex, row, key) => {
    const boldField = fieldBold === key[0];

    if (isRadioOption && columnIndex === 0) {
      return (
        <React.Fragment key={columnIndex}>
          <Radio
            label=""
            disabled={disabledRowCondition(row)}
            value="the value"
            onClick={() => onSelectedItem(rowIndex)}
            dataAt={'s'}
            selected={isRadioSelected === rowIndex}
          />
          <STableColumn addRadioSpace={false} isBold={boldField}>
            {getElement(row, key[0])}
          </STableColumn>
        </React.Fragment>
      );
    }

    if (key[0] === 'declineKey') {
      let linkString = row.declineKey;
      let declineKey;
      if (linkString.lastIndexOf) {
        let lastEqualSignIndex = linkString.lastIndexOf('=');
        declineKey = linkString.substring(lastEqualSignIndex + 1);
      } else {
        declineKey = getElement(row, key[0]);
      }

      return (
        <STableLink key={columnIndex} href={linkString}>
          {declineKey}
        </STableLink>
      );
    }

    return (
      <STableColumn addRadioSpace={false} key={columnIndex} isBold={boldField}>
        {getElement(row, key[0])}
      </STableColumn>
    );
  };

  const printTableBody = () =>
    children.map((row, rowIndex) => (
      <STableRow key={rowIndex} isHeader={false}>
        {tableTitlesList.map((key, columnIndex) => printTableColumn(rowIndex, columnIndex, row, key))}
      </STableRow>
    ));

  const printTable = () => (
    <STableContainer style={style} data-at={dataAt}>
      {printTableHeader()}
      {printTableBody()}
    </STableContainer>
  );

  const printUI = () => {
    if (children && children.length) {
      return printTable();
    }

    return <SEmptyTable>{emptyResultsText ? emptyResultsText : `${tableLabel} not fount`}</SEmptyTable>;
  };

  return printUI();
};
